import { AfterViewChecked, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Observable, of, from, Subscription } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import jsPDF from 'jspdf';
import moment from 'moment-timezone';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { SharedReportModel } from '../../models/shared-report.model';
import { ReportModel } from '../../models/report.model';
import { BlockModel } from '../../../PageBuilder/models/block.model';
import { SharedReportService } from '../../services/shared-report.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ReportsPageBuilderService } from '../../services/reports-page-builder.service';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { SharedReportSearchService } from '../../services/shared-report-search.service';
import { ReportBlockTypeEnum } from '../../enums/report-block-type.enum';
import { allowedFileTypes, FileUploadModel } from '../../../Forms/models/file-upload.model';
import { FileUploadComponent } from '../../../Forms/components/FileUpload/file-upload.component';
import { MediaServiceAccessTypeEnum } from '../../../../api/mediaService/enums/media-service-access-type.enum';
import { ProminenceRecalculationJobStatus } from '../../../../api/monitoring/monitoring-account-configuration.api';
import { ProminenceLockerService } from '../../../Misc/services/prominence-locker.service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { TabsStyleTypesEnum } from '../../../Layout/enums/tabs-types.enum';
import { agilityPrLogoDataModel } from '../../../Charts/models/agilitypr-logo-data.model';
import { chartColors } from '../../../Charts/enums/chart-colors.enum';
import { ChartBlockComponent } from '../ChartBlock/chart-block.component';
import { ProgressModel } from '../../../Dialogs/models/progress.model';
import { validateEmail, validateEmails } from '../../../Misc/helpers/formatting/email.helper';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { ScrollPanelComponent } from '../../../Basic/components/ScrollPanel/scroll-panel.component';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { dateFormats } from '../../../../Core/Users/data/date-formats';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { MediaSummaryModel } from '../../../../Common/ContentLibrary/models/content-library-media-summary.model';
import {
    ManageContentLibraryDialogComponent
} from '../../../../Common/ContentLibrary/components/ManageContentLibraryDialog/manage-content-library-dialog.component';
import * as htmlToImage from 'html-to-image';
import { Am5ChartBlockComponent } from '../Am5ChartBlock/am5-chart-block.component';
import { CanvasRenderer } from '@amcharts/amcharts5';


enum GenerateReportTabTypes {
    exportPDF,
    sharedReport
}

enum GenerateReportContentTypes {
    default,
    exportingPDF,
    editSharedReport,
    sendInvites
}

@Component({
    selector: 'agpr-generate-report-dialog',
    templateUrl: './generate-report-dialog.component.html',
    styleUrls: ['./generate-report-dialog.component.scss'],
    standalone: false
})
export class GenerateReportDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy, AfterViewChecked {
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() isSharedReportPdfExport: boolean = false;

    @Input() report: ReportModel;
    @Input() sharedReport: SharedReportModel;
    @Input() charts: BlockModel[];

    activeChart: BlockModel;
    coverImage: FileUploadModel[]; // Single file, but FileUploadComponent tracks an array
    uploadingCoverImage: boolean = false;
    defaultTitle: boolean = true;
    titleEmpty: boolean = false;
    coverTitleEmpty: boolean = false;
    duplicateTitle: boolean = false;
    saveLoading: boolean = false;
    prominenceStatus: ProminenceRecalculationJobStatus;
    prominenceStatuses = ProminenceRecalculationJobStatus;
    reportBlockTypeEnum = ReportBlockTypeEnum;
    userPreferenceTimezone: string;
    header: string = 'Reports.button.generateReport';
    defaultDateFormat: string;
    dateFormatOptions: DropdownOptionModel[];

    progress: ProgressModel = new ProgressModel();
    stopExportingPDF: boolean = false;
    exportingStatusString: string;

    hideSlideOverlay: boolean = false;

    readonly tabsStyleTypes = TabsStyleTypesEnum;
    selectedTabIndex: number = GenerateReportTabTypes.exportPDF;
    readonly generateReportTabTypes = GenerateReportTabTypes;
    readonly buttonTypes = ButtonTypesEnum;

    currentContentType = GenerateReportContentTypes.default;
    generateReportContentTypes = GenerateReportContentTypes;

    protected subscriptions: Subscription[] = [];
    protected uploadCoverImageRequest: Subscription;

    readonly imageMIMETypesRegex: string = allowedFileTypes.imageMIMETypesRegex;
    readonly imageMIMETypes: string = [
        ...allowedFileTypes.imageMIMETypesRegex.split('|').map(x => x[x.length - 1] === '$' ? x.substring(0, x.length - 1) : x),
    ].join(',');
    coverImageError: boolean = false;

    // Recipient chips component variables.
    splitOnPattern: RegExp = new RegExp('[,;]');
    readonly validateEmails: (string) => boolean = validateEmails;
    recipientsEmpty: boolean = false;
    invalidReplyTo: boolean = false;
    shareLoading: boolean = false;
    maxInputLength: number = 255;
    inputLengthExceeding: boolean = false;
    pageIndex = 0;

    @ViewChild('fileUpload') fileUpload: FileUploadComponent;
    @ViewChild('scrollPanel') setupScrollPanel: ScrollPanelComponent;
    @ViewChild('reportSetup') reportSetupElement: ElementRef;

    constructor(
        public coreDialogService: CoreDialogService,
        private reportsPageBuilderService: ReportsPageBuilderService,
        private sharedReportsService: SharedReportService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private sharedReportSearchService: SharedReportSearchService,
        private prominenceLockerService: ProminenceLockerService,
        private userStateService: CurrentUserStateService
    ) {
        super(coreDialogService, 'GenerateReport');

        this.progress.currentIndex = 0;
        this.progress.totalItems = 1;

        this.dateFormatOptions = dateFormats.map(format => new DropdownOptionModel(this.localizationService.transform(format.name), format.momentFormat));
    }

    ngOnInit(): void {
        const chartMappingNeeded = !this.sharedReport;

        if (!this.sharedReport) {
            this.sharedReport = new SharedReportModel();
            if (this.report) {
                this.sharedReport.coverTitle = this.report.title;
            }
            this.coverTitleEmpty = !this.sharedReport.coverTitle;
            this.sharedReport.reportId = this.report.id;
        }
        if (this.sharedReport.coverPageImageUrl) {
            this.coverImage = [
                {
                    id: this.sharedReport.coverPageImageUrl,
                    name: this.localizationService.transform('Discover.fileUpload.existing.image'),
                    size: 0,
                    type: 'N/A'
                },
            ];
        }

        if (!this.sharedReport.fromName) {
            this.userStateService.getUser().pipe(take(1)).subscribe(
                user => this.sharedReport.fromName = user.getDisplayName()
            );
        }
        if (!this.sharedReport.replyTo) {
            this.userStateService.getUser().pipe(take(1)).subscribe(
                user => this.sharedReport.replyTo = user.email
            );
        }

        if (!chartMappingNeeded) {
            this.setChartIndex();
            for (const chart of this.charts) {
                (chart.displayElement as ChartDisplayElementModel).enableEditingNote = true;
            }
        }

        let chartsInitialized = false;
        this.subscriptions.push(
            this.prominenceLockerService.prominenceStatus$.subscribe(status => {
                this.prominenceStatus = status;
                if (this.prominenceStatus !== ProminenceRecalculationJobStatus.DONE) {
                    // if prominence calculation is not completed, we do not allow users to create shared report with prominence charts
                    this.sharedReport.charts = this.sharedReport.charts.filter(chart => {
                        if (chart.type.key === ReportBlockTypeEnum.PROMINENCE_OVER_TIME || chart.type.key === ReportBlockTypeEnum.PROMINENCE_BY_REACH) {
                            (chart.displayElement as ChartDisplayElementModel).enableEditingNote = false;
                            return false;
                        } else {
                            return true;
                        }
                    });
                }
            }),
            this.userStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.userPreferenceTimezone = prefs.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                this.defaultDateFormat =  dateFormats.find(format => format.id === prefs.dateFormatId).momentFormat;
                this.sharedReport.dateFormat = this.defaultDateFormat;
                this.sharedReport.name = this.report?.name + ' - ' + moment().format(this.sharedReport.dateFormat);
                this.titleEmpty = !this.sharedReport.name;

                if (chartMappingNeeded && !chartsInitialized) {
                    chartsInitialized = true;
                    this.mapReportBlocksToCharts();
                    this.sharedReport.charts = this.charts.slice();
                    this.setChartIndex();
                    // enable editing note by default
                    for (const chart of this.charts) {
                        (chart.displayElement as ChartDisplayElementModel).enableEditingNote = true;
                    }
                }
            })
        );
    }

    ngAfterViewChecked(): void {
        if (this.setupScrollPanel?.contentDoesVerticalScroll() && !!this.reportSetupElement) {
            this.reportSetupElement.nativeElement.style.paddingRight = '10px';
        }
    }

    reportTitleChanged(value: string) {
        this.defaultTitle = false;
        this.titleEmpty = !value || value.trim().length === 0;
        if (this.selectedTabIndex === GenerateReportTabTypes.sharedReport ) {
            this.duplicateTitle = this.sharedReportSearchService.isTitleDuplicate(this.sharedReport.name, this.sharedReport?.id);
        }
    }

    openContentLibrary(): void {
        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
                selectionMode: true,
                useAcceptedFilesTypes: true,
                singleSelect: true,
                navBarLaunch: false
            },
            outputs: {
                dialogClose: (selectedFiles: MediaSummaryModel[]) => {
                    if (selectedFiles && selectedFiles.length > 0) {
                        const fileUpload: FileUploadModel = {
                            id: selectedFiles[0].id,
                            name: selectedFiles[0].name,
                            size: selectedFiles[0].size,
                            url: selectedFiles[0].url
                        };
                        this.coverImage = [fileUpload];
                    }
                }
            }
        };
        this.coreDialogService.open(ManageContentLibraryDialogComponent, io);
    }

    reportCoverTitleChanged(value: string) {
        this.coverTitleEmpty = !value || value.trim().length === 0;
    }

    createSharedReport() {
        this.titleEmpty = !this.sharedReport.name;
        if (this.titleEmpty) {
            return;
        }
        this.createOrUpdateReportStream('create').subscribe(res => {
            // if shared report is created successfully, change the page content to send invite form
            if (res) {
                this.currentContentType = GenerateReportContentTypes.sendInvites;
            }
        });
    }

    updateSharedReport() {
        this.titleEmpty = !this.sharedReport.name;
        if (this.titleEmpty) {
            return;
        }
        this.createOrUpdateReportStream('update').subscribe(() => {
            this.hideSlideOverlay = true;
        });
    }

    /**
     * Moves a chart and chart selected option up the list
     *
     * @param index - the series to move
     */
    moveChartUp(index: number) {
        if (index === 0) {
            return;
        }
        this.charts = this.moveUp(this.charts, index);
    }

    /**
     * Moves a chart and chart selected option down the list
     *
     * @param index - the series to move
     */
    moveChartDown(index: number): void {
        this.charts = this.moveDown(this.charts, index);
    }

    /**
     * Moves an item in an array down one position
     *
     * @param array - list to change order of
     * @param index - index of item to move down
     *
     * @returns any[] -  the array that was manipulated
     */
    moveDown(array: any[], index: number) {
        if (array && index === array.length - 1) {
            return;
        }
        const tempArray = array.slice();

        const itemToMoveDown = tempArray[index];
        const itemToMoveUp = tempArray[index + 1];

        tempArray.splice(index + 1, 1, itemToMoveDown);
        tempArray.splice(index, 1, itemToMoveUp);

        return tempArray;
    }

    /**
     * Moves an item in an array up one position
     *
     * @param array - list to change order of
     * @param index - index of item to move up
     *
     * @returns any[] -  the array that was manipulated
     */
    moveUp(array: any[], index: number): any[] {
        if (!array || index === 0) {
            return;
        }

        const tempArray = array.slice();

        const itemToMoveUp = tempArray[index];
        const itemToMoveDown = tempArray[index - 1];

        tempArray.splice(index, 1, itemToMoveDown);
        tempArray.splice(index - 1, 1, itemToMoveUp);

        return tempArray;
    }

    setChartIndex(): void {
        for (const [chartIndex, chart] of this.charts.entries()) {
            (chart.displayElement as ChartDisplayElementModel).index = chartIndex;
        }
    }

    setActiveChart(chart: BlockModel): void {
        this.activeChart = chart;
    }

    enableEditingNote(chartSelected: boolean, chart: BlockModel): void {
        if (chartSelected) {
            (chart.displayElement as ChartDisplayElementModel).enableEditingNote = true;
            this.sharedReport.charts.push(chart);
        } else {
            (chart.displayElement as ChartDisplayElementModel).enableEditingNote = false;
            this.sharedReport.charts = this.sharedReport.charts.filter(item => item.uniqueId !== chart.uniqueId);
        }
    }

    cancel(): void {
        if ( this.currentContentType = GenerateReportContentTypes.exportingPDF) {
            this.stopExportingPDF = true;
        }
        this.hideSlideOverlay = true;
    }

    mapReportBlocksToCharts() {
        this.charts = this.report.blocks.map(block => {
            const originalBlock = this.reportsPageBuilderService.getChartComponentFromBlock(block);
            const originalSeries = (block.displayElement as ChartDisplayElementModel).series;
            const chart = block.clone();
            // chart.id = undefined;
            const chartElement = chart.displayElement as ChartDisplayElementModel;
            const dateRange = chartElement.series[0].dateRange;

            if (dateRange.type === DateQueryTypeEnum.ALL) {
                chartElement.styles.allDateRange = true;
            } else {
                chartElement.styles.absoluteDateRange = DateQueryModel.makeAbsoluteRange(chartElement.series[0].dateRange, this.userPreferenceTimezone);
            }

            chartElement.styles.chartOptions = originalBlock?.chartOptions;

            if (originalBlock?.chart) {
                chartElement.styles.chartOptions = originalBlock.chart.options;
                chartElement.styles.chartMetadata = originalBlock.chart.metadata;
                if ((originalBlock.chart as any).chartSeries) {
                    chartElement.styles.chartSeries = (originalBlock.chart as any).chartSeries;
                }
            }

            if (chart.type.key === ReportBlockTypeEnum.COVERAGE_SUMMARY) {
                chartElement.styles.dateRangeLabelText = chartElement.dateRangeLabel;
            }

            chartElement.replaceAllSeries(originalSeries.map(series => {
                const newSeries = series.clone();
                newSeries.id = undefined;
                newSeries.dataProvider = series.dataProvider;
                newSeries.mentionProvider = series.mentionProvider;
                if (newSeries.subSeries?.length > 0) {
                    newSeries.styles.subSeries = newSeries.subSeries;
                }
                return newSeries;
            }));
            chartElement.originalReportBlockId = block.id;
            return chart;
        });
    }

    /**
     * Both creating and updating the report involve the same steps, so they can share the same stream
     */
    private createOrUpdateReportStream(actionType: ('create' | 'update')): Observable<number> {
        const isCreate = actionType === 'create';

        // Update the order from the charts, but only for the items that were selected
        this.sharedReport.charts = this.charts.filter(chart => this.sharedReport.charts.includes(chart));

        this.saveLoading = true;
        return this.uploadImageStream$().pipe(
            mergeMap(() => {
                if (isCreate) {
                    return this.sharedReportsService.createSharedReport(this.sharedReport).pipe(
                        tap(id => this.sharedReport.id = id)
                    );
                } else {
                    return this.sharedReportsService.updateSharedReport(this.sharedReport);
                }
            }),
            tap(id => {
                this.saveLoading = false;
                this.sharedReportSearchService.loadListAndSearch();
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform(`Reports.sharedReportSetupDialog.${actionType}.success.heading`),
                    detail: this.localizationService.transform(`Reports.sharedReportSetupDialog.${actionType}.success.message`, {
                        name: this.sharedReport.name
                    })
                });
            }),
            catchError(err => {
                let errorDetailMessage = `Reports.sharedReportSetupDialog.${actionType}.error.message`;
                if (err?.error?.match(/Shared Report with name .* already exists/)) {
                    this.duplicateTitle = true;
                    errorDetailMessage = 'Reports.sharedReportSetupDialog.create.error.duplicateName';
                    this.sharedReportSearchService.loadListAndSearch();
                }

                this.saveLoading = false;
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform(`Reports.sharedReportSetupDialog.${actionType}.error.heading`),
                    detail: this.localizationService.transform( errorDetailMessage, {
                        name: this.sharedReport.name
                    })
                });

                return of(null);
            })
        );
    }

    /**
     * A stream that uploads the cover image file if it exists
     * @returns void representing completion of uploading
     */
    private uploadImageStream$(): Observable<void> {
        const coverImageName = this.coverImage?.[0]?.name;
        if (coverImageName === this.localizationService.transform('Discover.fileUpload.existing.image')) {
            return of(null);
        } else {
            this.uploadingCoverImage = true;
            return from(this.fileUpload.uploadFiles(MediaServiceAccessTypeEnum.PUBLIC)).pipe(
                map(([coverImageFile]) => {
                    // This value can be undefined, which represents either:
                    //      an empty field or
                    //      a removal of an existing one
                    this.sharedReport.coverPageImageUrl = coverImageFile?.url;
                    this.uploadingCoverImage = false;
                }),
                catchError(error => {
                    this.uploadingCoverImage = false;
                    throw error;
                })
            );
        }
    }

    selectedTabChange(tab: GenerateReportTabTypes): void {
        this.selectedTabIndex = tab;
        if (this.selectedTabIndex === GenerateReportTabTypes.exportPDF) {
            this.duplicateTitle = false;
        }
    }

    close(overlayVisibility: boolean): void {
        if (!overlayVisibility) {
            this.closeDialog();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Exports the report to a PDF
     *
     * The numbers involved are all absolute positions inside a PDF:
     *      The default left/right margins are 70 px,
     *      The default top/bottom margins are 70 px
     *      The 'internal' page size is 594px by 459px (for letter sized)
     */
    public async exportPDF(): Promise<void> {
        this.coverTitleEmpty = !this.sharedReport.coverTitle;
        if (this.coverTitleEmpty) {
            return;
        }

        if (this.uploadCoverImageRequest) {
            this.uploadCoverImageRequest.unsubscribe();
            this.uploadCoverImageRequest = null;
        }

        this.uploadCoverImageRequest = this.uploadImageStream$().subscribe(async () => {
            this.header = 'Reports.generateReports.exportingReport';
            this.progress.totalItems = this.sharedReport.charts.length + 1;
            ++this.progress.currentIndex;
            this.currentContentType = GenerateReportContentTypes.exportingPDF;

            // Wait a single animation frame so that the charts have time to hide and the dialog is displayed
            await new Promise<void>(
                (resolve) => {
                    requestAnimationFrame(
                        () => {
                            resolve();
                        }
                    );
                });

            const doc = new jsPDF({
                orientation: 'landscape',
                format: 'letter',
                unit: 'px'
            });

            doc.setLanguage(this.localizationService.uiLanguageTag);

            // setting page width and height
            doc.internal.pageSize.width = 595;
            doc.internal.pageSize.height = 842;

            await this.addCoverPage(doc);

            if (this.coverImageError) {
                this.coverImageError = false;
                return;
            }


            // Update the order from the charts, but only for the items that were selected
            this.sharedReport.charts = this.charts.filter(chart => this.sharedReport.charts.includes(chart));


            // This block will add a page for each chart on the screen
            for (const block of this.sharedReport.charts) {

                const selectedBlock = this.isSharedReportPdfExport ? block : this.report.blocks.find(item => item.id === block.id);
                const chartComponent: ChartBlockComponent = this.reportsPageBuilderService.getChartComponentFromBlock(selectedBlock);

                const chartDisplayElement = block.displayElement as ChartDisplayElementModel;

                chartComponent.displayElement.displayAnnotationInExport = (block.displayElement as ChartDisplayElementModel).displayAnnotationInExport;

                ++this.progress.currentIndex;

                const anyInvalidSeries = !!chartDisplayElement.series.find(series => {
                    return !series.isValid();
                });

                if (anyInvalidSeries) {
                    continue;
                }

                if (!chartComponent) {
                    continue;
                }

                // uncomment this block of code if we want to generate plain note text only
                // let note = '';
                // // get plain text
                // if ((block?.displayElement as ChartDisplayElementModel)?.note) {
                //     const span = document.createElement('span');
                //     span.innerHTML = (block?.displayElement as ChartDisplayElementModel)?.note;
                //     note = span.textContent;
                // }

                if (this.stopExportingPDF) {
                    return;
                }

                this.exportingStatusString = (block.displayElement as ChartDisplayElementModel).title;
                await this.addChartPage(doc, chartComponent, (block?.displayElement as ChartDisplayElementModel)?.note);
            }

            const dateString = moment().format(this.sharedReport.dateFormat);
            if (this.report?.name) {
                doc.save(this.report.name + ' - ' + dateString + '.pdf');
            } else {
                doc.save(this.localizationService.transform('Reports.defaultFilename') + ' - ' + dateString + '.pdf');
            }

            // close the overlay after finishing PDF export
            this.hideSlideOverlay = true;
        });
    }

    /**
     * Adds a cover page to the doc, all elements absolutely positioned using pre-calculated magic numbers
     */
    private async addCoverPage(doc: jsPDF) {
        this.exportingStatusString = 'Reports.generateReports.exportingCoverPage';

        // set title font style and size
        doc.setFont('Helvetica', 'normal', 'lighter');
        doc.setFontSize(24);

        // Add cover page image
        let coverPageImageData = '';
        if (this.sharedReport && this.sharedReport.coverPageImageUrl) {
            coverPageImageData = await this.getDataImageFromUrl(this.sharedReport.coverPageImageUrl);
        }

        if (coverPageImageData !== '') {
            let coverPageImageProperties;
            try {
                coverPageImageProperties = doc.getImageProperties(coverPageImageData);
            } catch (error) {
                // cannot use image helper here because the same error will happen to addImage below which cannot be fixed
                // so exit with error here
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Reports.sharedReportInviteDialog.invite.coverImageError.message')
                });
                this.coverImageError = true;
                return;
            }

            // Page width is 595, page right/left padding is 70
            const maxWidth = 595 - 70 - 70;
            // Page height is 842, page top/bottom padding is 70, image top/bottom margin is 20, title area height is 50
            const maxHeight = 842 - 70 - 70 - 20 - 20 - 50;

            // 0.55949 was found through experimentation:
            // When I passed an image that was 622px wide, it became ~348px wide in the pdf
            // 348/622 = 0.55949, which is some sort of automatic internal scale ratio of JSPdf, it does not match
            // doc.internal.scaleRatio which is used in the addImage function, but isn't the whole story
            const correctedImageWidth = coverPageImageProperties.width * 0.55949;
            const correctedImageHeight = coverPageImageProperties.height * 0.55949;

            let scaledImageWidth = null;
            let scaledImageHeight = null;

            if (correctedImageWidth > maxWidth || correctedImageHeight > maxHeight) {
                if (correctedImageWidth > correctedImageHeight) {
                    // image is wider than it is tall
                    const ratio = coverPageImageProperties.width / maxWidth;
                    scaledImageWidth = maxWidth;
                    scaledImageHeight = coverPageImageProperties.height / ratio;
                } else if (correctedImageWidth <= correctedImageHeight) {
                    // image is taller than it is wide or square
                    const ratio = coverPageImageProperties.height / maxHeight;
                    scaledImageWidth = coverPageImageProperties.width / ratio;
                    scaledImageHeight = maxHeight;
                }
            }

            let coverImageXOffset = (doc.internal.pageSize.width / 2) - (correctedImageWidth / 2);
            if (scaledImageWidth) {
                coverImageXOffset = (doc.internal.pageSize.width / 2) - (scaledImageWidth / 2);
            }

            let coverImageYOffset = (doc.internal.pageSize.height / 2) - (correctedImageHeight / 2);
            if (scaledImageHeight) {
                coverImageYOffset = (doc.internal.pageSize.height / 2) - (scaledImageHeight / 2);
            }

            doc.addImage(
                coverPageImageData,
                '',                         // We don't know the image type, let the library figure it out
                coverImageXOffset,
                coverImageYOffset,
                scaledImageWidth,
                scaledImageHeight,
                '',
                'FAST'                      // use "fast" compression, least compression, fastest running
            );

            // print title, and vertically centered the title to the space left in top of the image
            // print title in the center
            // page padding top is 70

            let titleYOffset = 70 + ((doc.internal.pageSize.height - 140) / 2 - correctedImageHeight / 2) / 2;
            if (scaledImageHeight) {
                titleYOffset = 70 + ((doc.internal.pageSize.height - 140) / 2 - scaledImageHeight / 2) / 2;
            }

            doc.text(
                (this.sharedReport.coverTitle),
                (doc.internal.pageSize.width / 2),  // x offset
                titleYOffset,  // y offset
                {
                    align: 'center'
                }
            );
        } else {
            doc.text(
                (this.sharedReport.coverTitle),
                (doc.internal.pageSize.width / 2),   // x offset
                (doc.internal.pageSize.height / 2),  // y offset
                {
                    align: 'center'
                }
            );
        }

        // Output agility logo
        doc.addImage(
            agilityPrLogoDataModel,
            'PNG',
            (doc.internal.pageSize.width / 2) - 35,    // 110 px off the right side
            doc.internal.pageSize.height - 70,  // 70 px off the bottom
            75,                                 // I picked this as an arbitrary value
            27.37,                               // This is calculated based on scaling the width to 50, to keep the same ratio
            'agpr-logo',                        // setting an alias because this image is used multiple times (for efficiency)
            'FAST'
        );

    }

    /**
     * Adds a chart page to the doc, all elements absolutely positioned using pre-calculated magic numbers
     */
    private async addChartPage(doc: jsPDF, chartComponent: ChartBlockComponent, note: string) {
        this.pageIndex++;
        chartComponent.formatDateRangeLabel(this.sharedReport.dateFormat);

        let annotationLayer;
        if (!chartComponent.displayElement.displayAnnotationInExport) {
            const chart = (chartComponent as Am5ChartBlockComponent).am5Chart;
            const root = chart?.root;
            if (root) {
                annotationLayer = (root._renderer as CanvasRenderer).layers
                    .find(layer => layer.order === 1000);
                if (annotationLayer) {
                    annotationLayer.visible = false;
                }
            }
            chart?.series.forEach((chartSeries, index) => {
                const series = chart.amchart.series.getIndex(index);
                if (chartSeries.indicatorTargetIndex && series) {
                    series.hide();
                }
            });
        }

        const imageData = await chartComponent.getFullExportImage(800, 600, true);

        if (!chartComponent.displayElement.displayAnnotationInExport && annotationLayer) {
            annotationLayer.visible = true;
            const chart = (chartComponent as Am5ChartBlockComponent).am5Chart;
            chart?.series.forEach((chartSeries, index) => {
                const series = chart.amchart.series.getIndex(index);
                if (chartSeries.indicatorTargetIndex && series) {
                    series.show();
                }
            });
        }

        if (this.sharedReport.dateFormat !== this.defaultDateFormat) {
            chartComponent.formatDateRangeLabel(this.defaultDateFormat);
        }

        if (!imageData) {
            // Skip this page since the chart was not able to render
            return;
        }

        doc.addPage('a4', 'portrait');

        const pageWidth = 600;
        const pageHeight = 800;
        doc.internal.pageSize.width = pageWidth;
        doc.internal.pageSize.height = pageHeight;

        doc.setDrawColor(chartColors.agprWhite);
        doc.setFillColor(chartColors.agprWhite);

        const chartImageProperties = doc.getImageProperties(imageData);
        let scaledWidth = 474; // fallback
        let scaledHeight = 315; // fallback

        let x = 60;


        // If the image is too big, scale it down, we assume the ratio is good because our code controls that
        if ((chartComponent?.dataBlockType === ReportBlockTypeEnum.TOP_MENTIONS)
            || (chartComponent?.dataBlockType === ReportBlockTypeEnum.TOP_POSTS)) {
            const maxHeight = 730;
            const maxWidth = 475;
            if (chartImageProperties.height > maxHeight) {
                const scale = chartImageProperties.height / maxHeight;

                scaledHeight = maxHeight;
                scaledWidth = chartImageProperties.width / scale;
                if (scaledWidth > maxWidth) {
                    const scale2 = scaledWidth / maxWidth;

                    scaledWidth = maxWidth;
                    scaledHeight = scaledHeight / scale2;
                }
            } else if (chartImageProperties.width > maxWidth) {
                const scale = chartImageProperties.width / maxWidth;

                scaledWidth = maxWidth;
                scaledHeight = chartImageProperties.height / scale;
                if (scaledHeight > maxHeight) {
                    const scale2 = scaledHeight / maxHeight;

                    scaledHeight = maxHeight;
                    scaledWidth = scaledWidth / scale2;
                }
            }
        } else if (chartComponent?.dataBlockType === ReportBlockTypeEnum.TOP_TERMS) {
            const maxWidth = 474;
            const maxHeight = 315;
            if (chartImageProperties.width > maxWidth || chartImageProperties.height > maxHeight) {
                const widthScale = chartImageProperties.width / maxWidth;
                const heightScale = chartImageProperties.height / maxHeight;
                const scale = Math.max(widthScale, heightScale);
                scaledWidth = chartImageProperties.width / scale;
                scaledHeight = chartImageProperties.height / scale;
                x = (pageWidth - scaledWidth) / 2;
            } else {
                scaledWidth = chartImageProperties.width;
                scaledHeight = chartImageProperties.height;
            }
        } else if (chartComponent?.dataBlockType === ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME
            || chartComponent?.dataBlockType === ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_COMPARISON) {
            scaledWidth = 474;
            scaledHeight = Math.round(scaledWidth * chartImageProperties.height / chartImageProperties.width);
        } else if (chartImageProperties.width > 800 || chartImageProperties.height > 600) {
            scaledWidth = 474;
            scaledHeight = 315;
        }

        doc.roundedRect(
            60,             // x offset
            80,             // y offset
            474,            // width
            scaledHeight,   // height matches the chart
            3,              // left/right corner rounding
            3,              // top/bottom corner rounding
            'DF'            // Color both the stroke and fill
        );

        // Add the chart
        doc.addImage(
            imageData,
            'PNG',
            x,     // x offset
            60,     // y offset
            scaledWidth,
            scaledHeight,
            '',     // no alias as image isn't re-used
            'SLOW'  // use "slow" compression, most compression, slowest running
        );

        // Add note if present
        if (note) {
            // Draw a line above the note
            const noteYStart = scaledHeight + 70;
            doc.setLineWidth(1);
            doc.setDrawColor(chartColors.agprLightGrey);
            doc.line(70, noteYStart, pageWidth - 70, noteYStart);
            // Create container
            const container = document.createElement('div');
            container.style.width = '355px';
            container.style.position = 'absolute';
            container.style.left = '0';
            container.style.top = '0';
            container.style.zIndex = '-9999';     // to hide the container from the user screen
            container.style.paddingBottom = '8px';
            container.innerHTML = note;
            document.body.appendChild(container);
            // Measure safely
            const width = container.offsetWidth || 355;
            const height = container.offsetHeight + 5 || 205; // fallback if 0
            const imgHeight = (355 * height) / width;
            // Render to image
            const imgData = await htmlToImage.toJpeg(container, {
                quality: 1,
                pixelRatio: 2,
                backgroundColor: 'white',  // force white background
            });
            document.body.removeChild(container);


            // Check if note fits on current page, else add a new page
            let noteY = noteYStart + 10;
            if (noteY + imgHeight > pageHeight - 50) {
                doc.addPage();
                noteY = 50;
            }

            doc.addImage(imgData, 'JPEG', 70, noteY, 355, imgHeight);
        }
    }

    /**
     * Fetch an image and return it as a base64 encoded data url (async)
     */
    private async getDataImageFromUrl(url: string): Promise<string> {
        return new Promise<string>(
            resolve => {
                fetch(url)
                    .then(response => response.blob())
                    .then(blob => {
                        const reader = new FileReader();
                        reader.onload = function() {
                            resolve(this.result.toString());
                        };
                        reader.readAsDataURL(blob);
                    })
                    .catch( error => {
                        resolve('');
                    });
            }
        );
    }


    checkRecipients() {
        return this.recipientsEmpty = this.sharedReport.recipientList.length === 0 && this.sharedReport.mailingListIds.length === 0;
    }

    validateReplyTo() {
        this.invalidReplyTo = this.sharedReport.replyTo && !validateEmail(this.sharedReport.replyTo);
    }

    async sendInvites(): Promise<void> {
        this.checkRecipients();
        try {
            if (!this.recipientsEmpty && !this.invalidReplyTo) {
                this.shareLoading = true;
                // update shared report before sending emails
                await this.sharedReportsService.updateSharedReport(this.sharedReport).toPromise();
                // send invite emails
                await this.sharedReportsService.sendInvites(this.sharedReport).toPromise();
                this.closeDialog();
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Reports.sharedReportInviteDialog.invite.success.heading'),
                    detail: this.localizationService.transform('Reports.sharedReportInviteDialog.invite.success.message', {
                        name: this.sharedReport.name
                    })
                });
            }
        } catch (err) {
            this.shareLoading = false;
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Reports.sharedReportInviteDialog.invite.error.heading'),
                detail: this.localizationService.transform('Reports.sharedReportInviteDialog.invite.error.message', {
                    name: this.sharedReport.name
                })
            });
        }
    }
    checkInputLength(event){
        if (event) {
            this.inputLengthExceeding = event.length === this.maxInputLength;
        }
    }

    updateReportName() {
        if (this.defaultTitle) {
            this.sharedReport.name = this.report.name + ' - ' + moment().format(this.sharedReport.dateFormat);
            this.titleEmpty = !this.sharedReport.name;
        }
    }
}
